import {
  Avatar,
  Box,
  Stack,
  Typography,
  Card,
  CardContent,
  Divider,
  Chip,
  Skeleton,
  Container,
} from '@mui/material';
import {
  Business,
  LocationOn,
  Schedule,
  LocalShipping,
  Person,
  VerifiedUser,
  LocalPhone,
  InfoOutlined,
  Category,
  FitnessCenter,
} from '@mui/icons-material';
import { alpha, useTheme } from '@mui/material/styles';
import React, { useEffect, useState } from 'react';
import { getUrl } from 'src/components/PreviewDocument';
import { ChatWithParticipants } from 'src/components/dashboard/details/Donation';
import useAuth from 'src/hooks/useAuth';
import useGetCurrentRescueDriver from 'src/hooks/useGetCurrentRescueDriver';
import { useTranslation } from 'react-i18next';
import { capitalize } from 'lodash';
import { Weight } from 'careit';
import { useBranding } from 'src/contexts/BrandContext';
const CareitId = () => {
  const branding = useBranding();
  const { user } = useAuth();
  const { t } = useTranslation('translation');
  const theme = useTheme();
  const [geolocationChecked, setGeolocationChecked] = useState(false);
  const [location, setLocation] = useState(null);
  const { data: rescueData, isPending: isLoadingCurrentRescue } =
    useGetCurrentRescueDriver({
      lat: location?.lat,
      lng: location?.lng,
      enabled: geolocationChecked,
    });

  const rescues = rescueData?.rescues ?? [];
  const hasRescues = rescues.length > 0;

  useEffect(() => {
    // first check if geolocation is available, then mark it as checked and continue making the request
    if (!navigator.geolocation) {
      setGeolocationChecked(true);
      return;
    }

    const options = {
      enableHighAccuracy: true,
      timeout: 10000,
      maximumAge: 60000,
    };

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const { latitude, longitude } = position.coords;
        setLocation({ lat: latitude, lng: longitude });
        setGeolocationChecked(true);
      },
      (error) => {
        setGeolocationChecked(true);
        console.log('Geolocation error:', error);
      },
      options
    );
  }, []);

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default', pb: 8 }}>
      <Container maxWidth="sm" sx={{ pt: { xs: 4, md: 8 } }}>
        {/* ID BADGE */}
        <Card
          data-tour="careit-id-header"
          sx={{
            position: 'relative',
            overflow: 'hidden',
            borderRadius: 4,
            boxShadow: theme.customShadows?.z24 || theme.shadows[20],
            background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.darker || '#0c2723'} 100%)`,
            color: 'common.white',
            mb: 4,
          }}
        >
          {/* Decorative Background Elements */}
          <Box
            sx={{
              position: 'absolute',
              top: -80,
              right: -50,
              width: 250,
              height: 250,
              borderRadius: '50%',
              background: `radial-gradient(circle, ${alpha('#fff', 0.15)} 0%, ${alpha('#fff', 0)} 70%)`,
            }}
          />
          <Box
            sx={{
              position: 'absolute',
              bottom: -60,
              left: -60,
              width: 200,
              height: 200,
              borderRadius: '50%',
              background: `radial-gradient(circle, ${alpha('#fff', 0.1)} 0%, ${alpha('#fff', 0)} 70%)`,
            }}
          />

          <Box sx={{ position: 'relative', p: { xs: 3, sm: 4 } }}>
            {/* Header: Logo and Verified Badge */}
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="flex-start"
              mb={4}
            >
              {user?.business_logo_url ? (
                <Box
                  sx={{
                    bgcolor: 'common.white',
                    borderRadius: 1.5,
                    p: 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                    height: 48,
                    minWidth: 48,
                    maxWidth: 120, // allows variable width up to a max
                  }}
                >
                  <Box
                    component="img"
                    src={getUrl(user.business_logo_url)}
                    sx={{
                      maxHeight: '100%',
                      maxWidth: '100%',
                      objectFit: 'contain',
                    }}
                  />
                </Box>
              ) : (
                <Box /> /* Empty box to maintain flex-between spacing */
              )}
              <Chip
                icon={
                  <VerifiedUser
                    sx={{
                      color: '#fff !important',
                      fontSize: '18px !important',
                    }}
                  />
                }
                label={t('mobile.careit-id.verified', 'VERIFIED')}
                size="small"
                sx={{
                  bgcolor: alpha('#fff', 0.2),
                  color: '#fff',
                  backdropFilter: 'blur(8px)',
                  fontWeight: 700,
                  letterSpacing: 1.2,
                  boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                  padding: '2px 4px',
                }}
              />
            </Stack>

            {/* Main Identity Area */}
            <Stack alignItems="center" spacing={2.5} mb={4}>
              <Avatar
                src={user?.image ? getUrl(user.image) : null}
                sx={{
                  width: 140,
                  height: 140,
                  border: '4px solid',
                  borderColor: alpha('#fff', 0.8),
                  boxShadow: '0 8px 24px rgba(0,0,0,0.3)',
                  bgcolor: 'background.paper',
                  color: 'text.secondary',
                }}
              />
              <Box textAlign="center">
                <Typography
                  variant="overline"
                  sx={{
                    opacity: 0.8,
                    letterSpacing: 2.5,
                    display: 'block',
                    mb: 0.5,
                  }}
                >
                  {t('mobile.careit-id.authorized-driver', 'AUTHORIZED DRIVER')}
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontWeight: 800,
                    mb: 1,
                    textShadow: '0 2px 4px rgba(0,0,0,0.2)',
                  }}
                >
                  {user?.name}
                </Typography>
                <Typography variant="h6" sx={{ fontWeight: 500, opacity: 0.9 }}>
                  {user?.business_name}
                </Typography>
              </Box>
            </Stack>

            {/* Footer / Branding Area */}
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="center"
              sx={{
                p: 2,
                borderRadius: 2,
                bgcolor: alpha('#fff', 0.08),
                backdropFilter: 'blur(10px)',
                border: `1px solid ${alpha('#fff', 0.1)}`,
              }}
            >
              <Typography
                variant="overline"
                sx={{ opacity: 0.7, letterSpacing: 1.5, mr: 1, pt: 0.5 }}
              >
                {t('mobile.careit-id.powered-by')}
              </Typography>
              <Box
                component="img"
                alt={branding.displayName}
                src={branding.logoUrl || '/static/brand/careit_text_white.png'}
                sx={{
                  height: 16,
                  filter: 'drop-shadow(0px 1px 2px rgba(0,0,0,0.1))',
                  opacity: 0.9,
                }}
              />
            </Stack>
          </Box>
        </Card>

        {/* LOADING STATE */}
        {isLoadingCurrentRescue && (
          <Box>
            <Typography variant="h6" sx={{ mb: 3, fontWeight: 700 }}>
              {t('mobile.careit-id.active-assignments')}
            </Typography>
            <Card
              data-tour="careit-id-empty-state"
              sx={{ borderRadius: 3, boxShadow: theme.shadows[3] }}
            >
              <CardContent sx={{ p: 4 }}>
                <Box sx={{ mb: 4 }}>
                  <Skeleton
                    variant="text"
                    width="60%"
                    height={48}
                    sx={{ mb: 1 }}
                  />
                  <Skeleton
                    variant="rounded"
                    width={120}
                    height={28}
                    borderRadius={2}
                  />
                </Box>
                <Divider sx={{ mb: 3 }} />
                {[...Array(3)].map((_, index) => (
                  <Stack key={index} direction="row" spacing={2} sx={{ mb: 3 }}>
                    <Skeleton variant="circular" width={28} height={28} />
                    <Box sx={{ width: '100%' }}>
                      <Skeleton
                        variant="text"
                        width="40%"
                        height={20}
                        sx={{ mb: 0.5 }}
                      />
                      <Skeleton variant="text" width="80%" height={24} />
                    </Box>
                  </Stack>
                ))}
              </CardContent>
            </Card>
          </Box>
        )}

        {/* NO RESCUES STATE */}
        {!hasRescues && !isLoadingCurrentRescue && geolocationChecked && (
          <Card
            data-tour="careit-id-assignment-card"
            sx={{
              borderRadius: 3,
              boxShadow: theme.shadows[2],
              bgcolor: 'background.paper',
              p: 4,
              textAlign: 'center',
            }}
          >
            <Typography
              variant="h6"
              color="text.secondary"
              sx={{ fontWeight: 500 }}
            >
              {t('mobile.careit-id.no-pickup-currently-scheduled')}
            </Typography>
          </Card>
        )}

        {/* ACTUAL RESCUES */}
        {hasRescues && !isLoadingCurrentRescue && (
          <Box>
            <Typography
              variant="h6"
              sx={{ mb: 3, fontWeight: 700, color: 'text.primary', pl: 1 }}
            >
              {t('mobile.careit-id.active-assignments')}
            </Typography>

            <Stack spacing={3}>
              {rescues.map((rescue, index) => {
                const isFoodRun = rescue.is_food_run;
                return (
                  <Card
                    key={rescue.rescue_id || rescue.donation_id || index}
                    data-tour={
                      index === 0 ? 'careit-id-assignment-card' : undefined
                    }
                    sx={{
                      borderRadius: 3,
                      boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
                      border: '1px solid',
                      borderColor: 'grey.200',
                      transition: 'all 0.2s ease-in-out',
                      bgcolor: 'background.paper',
                      '&:hover': {
                        transform: 'translateY(-2px)',
                        boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
                        borderColor: 'primary.main',
                      },
                    }}
                  >
                    <CardContent
                      sx={{
                        p: { xs: 2.5, sm: 3 },
                        '&:last-child': { pb: 2.5 },
                      }}
                    >
                      {/* Rescue Header */}
                      <Stack
                        direction="row"
                        justifyContent="space-between"
                        alignItems="flex-start"
                        mb={2.5}
                      >
                        <Box sx={{ pr: 2 }}>
                          <Typography
                            variant="subtitle1"
                            sx={{ fontWeight: 700, mb: 0.5, lineHeight: 1.2 }}
                          >
                            {rescue.title}
                          </Typography>
                          <Stack
                            direction="row"
                            spacing={1}
                            alignItems="center"
                            mt={1}
                          >
                            <Chip
                              label={
                                isFoodRun
                                  ? t('mobile.careit-id.food-run')
                                  : t('mobile.careit-id.donation')
                              }
                              size="small"
                              sx={{
                                fontWeight: 700,
                                letterSpacing: 0.5,
                                borderRadius: 1.5,
                                height: 22,
                                px: 0.5,
                                bgcolor: isFoodRun
                                  ? alpha(theme.palette.info.main, 0.12)
                                  : alpha(theme.palette.success.main, 0.12),
                                color: isFoodRun ? 'info.dark' : 'success.dark',
                                '& .MuiChip-label': { fontSize: '0.7rem' },
                              }}
                            />
                            <Typography
                              variant="caption"
                              sx={{
                                color: 'text.secondary',
                                fontWeight: 600,
                                display: 'flex',
                                alignItems: 'center',
                                gap: 0.5,
                                textTransform: 'capitalize',
                              }}
                            >
                              <LocalShipping
                                fontSize="inherit"
                                sx={{ opacity: 0.7 }}
                              />
                              {t(
                                `mobile.careit-id.delivery-option.${rescue.delivery_option.toLowerCase()}`,
                                rescue.delivery_option
                                  .split('-')
                                  .map((str) => capitalize(str))
                                  .join(' ')
                              )}
                            </Typography>
                          </Stack>
                        </Box>
                      </Stack>

                      {/* Info Layout */}
                      <Stack spacing={2}>
                        {/* Address */}
                        <Stack
                          data-tour={
                            index === 0 ? 'careit-id-pickup-address' : undefined
                          }
                          direction="row"
                          spacing={1.5}
                          alignItems="flex-start"
                        >
                          <LocationOn
                            color="action"
                            fontSize="small"
                            sx={{ mt: 0.25 }}
                          />
                          <Box flex={1}>
                            <Typography
                              variant="caption"
                              sx={{
                                color: 'text.secondary',
                                fontWeight: 600,
                                textTransform: 'uppercase',
                                display: 'block',
                                mb: 0.25,
                                lineHeight: 1.2,
                              }}
                            >
                              {t('mobile.careit-id.pickup-address')}
                            </Typography>
                            <Typography
                              variant="body2"
                              sx={{ fontWeight: 500, lineHeight: 1.3 }}
                            >
                              {rescue.pickup_address}
                            </Typography>
                          </Box>
                        </Stack>

                        {/* Date and Recipient (Side-by-side) */}
                        <Stack
                          direction="row"
                          spacing={2}
                          sx={{ flexWrap: 'wrap', gap: 2 }}
                        >
                          <Stack
                            direction="row"
                            spacing={1.5}
                            alignItems="flex-start"
                            data-tour={
                              index === 0
                                ? 'careit-id-pickup-datetime'
                                : undefined
                            }
                            sx={{ minWidth: 140, flex: 1 }}
                          >
                            <Schedule
                              color="action"
                              fontSize="small"
                              sx={{ mt: 0.25 }}
                            />
                            <Box w="100%">
                              <Typography
                                variant="caption"
                                sx={{
                                  color: 'text.secondary',
                                  fontWeight: 600,
                                  textTransform: 'uppercase',
                                  display: 'block',
                                  mb: 0.25,
                                  lineHeight: 1.2,
                                }}
                              >
                                {t('mobile.careit-id.pickup-date-time')}
                              </Typography>
                              <Typography
                                variant="body2"
                                sx={{ fontWeight: 500, lineHeight: 1.3 }}
                              >
                                {new Date(rescue.start_date).toLocaleString(
                                  [],
                                  { dateStyle: 'medium', timeStyle: 'short' }
                                )}
                              </Typography>
                            </Box>
                          </Stack>

                          {rescue.recipient_team_id !== user?.team_id && (
                            <Stack
                              direction="row"
                              spacing={1.5}
                              alignItems="flex-start"
                              data-tour={
                                index === 0
                                  ? 'careit-id-recipient-organization'
                                  : undefined
                              }
                              sx={{ minWidth: 140, flex: 1 }}
                            >
                              <Business
                                color="action"
                                fontSize="small"
                                sx={{ mt: 0.25 }}
                              />
                              <Box w="100%">
                                <Typography
                                  variant="caption"
                                  sx={{
                                    color: 'text.secondary',
                                    fontWeight: 600,
                                    textTransform: 'uppercase',
                                    display: 'block',
                                    mb: 0.25,
                                    lineHeight: 1.2,
                                  }}
                                >
                                  {t('mobile.careit-id.recipient-organization')}
                                </Typography>
                                <Typography
                                  variant="body2"
                                  sx={{ fontWeight: 500, lineHeight: 1.3 }}
                                >
                                  {rescue.recipient_organization || 'Unknown'}
                                </Typography>
                              </Box>
                            </Stack>
                          )}
                        </Stack>

                        {/* Contents and Contact (Side-by-side) */}
                        {(rescue.food_types ||
                          rescue.donor_contact_name ||
                          rescue.pickup_instructions) && (
                          <>
                            <Stack
                              direction="row"
                              spacing={2}
                              sx={{ flexWrap: 'wrap', gap: 2 }}
                            >
                              {rescue.food_types && (
                                <Stack
                                  direction="row"
                                  spacing={1.5}
                                  alignItems="flex-start"
                                  sx={{ minWidth: 140, flex: 1 }}
                                >
                                  <Category
                                    color="action"
                                    fontSize="small"
                                    sx={{ mt: 0.25 }}
                                  />
                                  <Box w="100%">
                                    <Typography
                                      variant="caption"
                                      sx={{
                                        color: 'text.secondary',
                                        fontWeight: 600,
                                        textTransform: 'uppercase',
                                        display: 'block',
                                        mb: 0.25,
                                        lineHeight: 1.2,
                                      }}
                                    >
                                      {t('mobile.careit-id.donation-contents')}
                                    </Typography>
                                    <Typography
                                      variant="body2"
                                      sx={{ fontWeight: 500, lineHeight: 1.3 }}
                                    >
                                      {rescue.food_types}
                                    </Typography>
                                  </Box>
                                </Stack>
                              )}

                              {rescue.donor_contact_name && (
                                <Stack
                                  direction="row"
                                  spacing={1.5}
                                  alignItems="flex-start"
                                  sx={{ minWidth: 140, flex: 1 }}
                                >
                                  <LocalPhone
                                    color="action"
                                    fontSize="small"
                                    sx={{ mt: 0.25 }}
                                  />
                                  <Box w="100%">
                                    <Typography
                                      variant="caption"
                                      sx={{
                                        color: 'text.secondary',
                                        fontWeight: 600,
                                        textTransform: 'uppercase',
                                        display: 'block',
                                        mb: 0.25,
                                        lineHeight: 1.2,
                                      }}
                                    >
                                      {t('mobile.careit-id.pickup-contact')}
                                    </Typography>
                                    <Typography
                                      variant="body2"
                                      sx={{ fontWeight: 500, lineHeight: 1.3 }}
                                    >
                                      {rescue.donor_contact_name}{' '}
                                      {rescue.donor_contact_phone &&
                                        `(${rescue.donor_contact_phone})`}
                                    </Typography>
                                  </Box>
                                </Stack>
                              )}
                            </Stack>

                            {/* Total Weight */}
                            {rescue.total_weight > 0 && (
                              <Stack
                                direction="row"
                                spacing={1.5}
                                alignItems="flex-start"
                              >
                                <FitnessCenter
                                  color="action"
                                  fontSize="small"
                                  sx={{ mt: 0.25 }}
                                />
                                <Box w="100%">
                                  <Typography
                                    variant="caption"
                                    sx={{
                                      color: 'text.secondary',
                                      fontWeight: 600,
                                      textTransform: 'uppercase',
                                      display: 'block',
                                      mb: 0.25,
                                      lineHeight: 1.2,
                                    }}
                                  >
                                    {t('mobile.careit-id.total-weight')}
                                  </Typography>
                                  <Typography
                                    variant="body2"
                                    sx={{ fontWeight: 500, lineHeight: 1.3 }}
                                  >
                                    {new Weight(
                                      rescue.total_weight,
                                      'lbs'
                                    ).toString()}
                                  </Typography>
                                </Box>
                              </Stack>
                            )}

                            {/* Pickup Instructions */}
                            {rescue.pickup_instructions && (
                              <Stack
                                direction="row"
                                spacing={1.5}
                                alignItems="flex-start"
                              >
                                <InfoOutlined
                                  color="action"
                                  fontSize="small"
                                  sx={{ mt: 0.25 }}
                                />
                                <Box flex={1}>
                                  <Typography
                                    variant="caption"
                                    sx={{
                                      color: 'text.secondary',
                                      fontWeight: 600,
                                      textTransform: 'uppercase',
                                      display: 'block',
                                      mb: 0.25,
                                      lineHeight: 1.2,
                                    }}
                                  >
                                    {t('mobile.careit-id.pickup-instructions')}
                                  </Typography>
                                  <Typography
                                    variant="body2"
                                    sx={{ fontWeight: 500, lineHeight: 1.3 }}
                                  >
                                    {rescue.pickup_instructions}
                                  </Typography>
                                </Box>
                              </Stack>
                            )}
                          </>
                        )}

                        <Divider sx={{ borderStyle: 'dashed' }} />

                        {/* Created By and Chat */}
                        <Stack
                          direction="row"
                          justifyContent="space-between"
                          alignItems="flex-end"
                        >
                          <Stack
                            data-tour={
                              index === 0 ? 'careit-id-created-by' : undefined
                            }
                            direction="row"
                            spacing={1.5}
                            alignItems="flex-start"
                          >
                            <Person
                              color="action"
                              fontSize="small"
                              sx={{ mt: 0.25 }}
                            />
                            <Box flex={1}>
                              <Typography
                                variant="caption"
                                sx={{
                                  color: 'text.secondary',
                                  fontWeight: 600,
                                  textTransform: 'uppercase',
                                  display: 'block',
                                  mb: 0.25,
                                  lineHeight: 1.2,
                                }}
                              >
                                {isFoodRun
                                  ? t('mobile.careit-id.food-run-created-by')
                                  : t('mobile.careit-id.donation-created-by')}
                              </Typography>
                              <Typography
                                variant="body2"
                                sx={{ fontWeight: 500, lineHeight: 1.3 }}
                              >
                                {rescue.donation_created_by}
                              </Typography>
                            </Box>
                          </Stack>

                          {rescue.donation_id && (
                            <Box>
                              <ChatWithParticipants
                                donation={{
                                  id: rescue.donation_id,
                                  conversation_id: rescue.conversation_id,
                                  rescue_id: rescue.rescue_id,
                                  title: rescue.title,
                                }}
                              />
                            </Box>
                          )}
                        </Stack>
                      </Stack>
                    </CardContent>
                  </Card>
                );
              })}
            </Stack>
          </Box>
        )}
      </Container>
    </Box>
  );
};

export default CareitId;
